<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { wheelUtils } from '$lib/utils/wheel';
  
  export let names = [];
  export let colors = [];
  
  let canvas;
  let ctx;
  let canvasSize = 400;
  let currentRotation = 0;
  let isSpinning = false;
  
  const dispatch = createEventDispatcher();
  
  let textWidthCache = {};
  function getTextWidth(text, font) {
    const key = `${font}:${text}`;
    if (textWidthCache[key]) return textWidthCache[key];
    ctx.font = font;
    const width = ctx.measureText(text).width;
    textWidthCache[key] = width;
    return width;
  }
  
  // For better initial loading performance
  let initialRenderComplete = false;
  
  // Helper function to check if code is running in browser
  function isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }
  
  // Export redraw function to allow parent components to trigger redraws
  export function redraw() {
    if (!ctx || !canvas) return;
    
    // Use requestAnimationFrame for smooth rendering
    requestAnimationFrame(drawWheel);
    return true;
  }

  onMount(() => {
    ctx = canvas.getContext('2d');
    
    // Handle responsive canvas sizing
    const resizeCanvas = () => {
      const containerWidth = canvas.parentElement.clientWidth;
      const isMobile = window.innerWidth < 768;
      canvasSize = Math.min(containerWidth, isMobile ? 320 : 500);
      canvas.width = canvasSize;
      canvas.height = canvasSize;
      
      // Limit DPR on mobile for performance
      let dpr = window.devicePixelRatio || 1;
      if (isMobile && dpr > 2) dpr = 2;
      if (dpr > 1) {
        canvas.style.width = `${canvasSize}px`;
        canvas.style.height = `${canvasSize}px`;
        canvas.width = canvasSize * dpr;
        canvas.height = canvasSize * dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform before scaling
        ctx.scale(dpr, dpr);
      }
      
      drawWheel();
    };
    
    // Use requestAnimationFrame for smoother resizing
    window.addEventListener('resize', () => {
      requestAnimationFrame(resizeCanvas);
    });
    resizeCanvas();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  });
  
  // Optimize reactive statements to batch redraws
  let pendingDraw = false;
  let prevNamesLength = 0;
  
  $: if (ctx && (names || colors) && !pendingDraw && initialRenderComplete) {
    pendingDraw = true;
    requestAnimationFrame(() => {
      drawWheel();
      pendingDraw = false;
    });
  }
  
  // Additional reactive statement to detect array length changes
  $: if (ctx && names && names.length !== prevNamesLength) {
    prevNamesLength = names.length;
    if (!pendingDraw) {
      pendingDraw = true;
      requestAnimationFrame(() => {
        drawWheel();
        pendingDraw = false;
      });
    }
  }
  
  function drawWheel() {
    if (!ctx) return;
    
    // Performance optimization: use performance.now for tracking render time
    const startTime = performance.now();
    
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    const radius = canvasSize / 2 - 10;
    
    // Cache expensive calculations and browser queries
    const deviceWidth = window.innerWidth;
    const isMobile = deviceWidth < 768;
    
    // Draw wheel segments
    if (names.length > 0) {
      const arc = (2 * Math.PI) / names.length;
      
      // Optimize font size calculation - do once outside the loop
      const fontSizeBase = isMobile ? 10 : 12;
      const maxFontSize = isMobile ? 14 : 18;
      const fontSize = Math.max(fontSizeBase, Math.min(maxFontSize, 400 / names.length));
      ctx.font = `bold ${fontSize}px Arial`;
      
      // Pre-calculate common values
      const textRadiusOffset = isMobile ? 15 : 20;
      
      // Performance optimization: Use batch drawing for names > 20
      // For mobile with many segments, skip half the text labels
      const drawEveryNth = (isMobile && names.length > 40) ? 4 : 
                         (isMobile && names.length > 20) ? 2 : 1;
      
      // Draw all segments first in a batch (more efficient than mixing fill and stroke)
      for (let i = 0; i < names.length; i++) {
        const angle = i * arc;
        const sliceColor = colors[i % colors.length] || wheelUtils.getRandomColor(i);
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, angle + currentRotation, angle + arc + currentRotation);
        ctx.closePath();
        ctx.fillStyle = sliceColor;
        ctx.fill();
      }
      
      // Draw all strokes in a batch
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      for (let i = 0; i < names.length; i++) {
        const angle = i * arc;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, angle + currentRotation, angle + arc + currentRotation);
        ctx.closePath();
        ctx.stroke();
      }
      
      // Draw text in a batch (more efficient)
      ctx.textAlign = 'right';
      ctx.fillStyle = '#fff';
      
      // Limit drawing frequency on low-end devices
      // Mobile devices with > 40 segments will only show every 4th name
      const skipDisplayReasons = {
        tooManyMobile: isMobile && names.length > 20 && drawEveryNth > 1
      };
      
      // Display critical performance testing info during dev
      /*if (isMobile && names.length > 20) {
        console.log(`Skip display: ${drawEveryNth}, wheelSize: ${radius}`);
      }*/
      
      // Draw the labels only if we have a manageable amount or according to our skip rate
      for (let i = 0; i < names.length; i++) {
        // Skip drawing text based on our calculated frequency
        if (i % drawEveryNth !== 0 && skipDisplayReasons.tooManyMobile) continue;
        
        const angle = i * arc;
        const name = names[i];
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(angle + arc / 2 + currentRotation);
        
        // Fast text truncation for better performance
        let displayName = name;
        if (name.length > 10) {
          // Fixed truncation length rather than measuring width each time
          const maxChars = isMobile ? 8 : 15;
          displayName = name.slice(0, maxChars) + (name.length > maxChars ? '...' : '');
        }
        
        ctx.fillText(displayName, radius - textRadiusOffset, 5);
        ctx.restore();
      }
    } else {
      // Draw empty wheel - simplified for better performance
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#f0f0f0';
      ctx.fill();
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw text
      ctx.fillStyle = '#666666';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Add names to spin the wheel', centerX, centerY);
    }
    
    // Draw center
    ctx.beginPath();
    ctx.arc(centerX, centerY, 16, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Draw pointer with simpler path
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - radius + 10);  // Tip of the arrow
    ctx.lineTo(centerX - 10, centerY - radius - 10);  // Left point 
    ctx.lineTo(centerX + 10, centerY - radius - 10);  // Right point
    ctx.lineTo(centerX, centerY - radius + 10);  // Back to tip to close
    ctx.fillStyle = '#ff4444';
    ctx.fill();
    ctx.strokeStyle = '#cc0000';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Debug performance when needed
    // const duration = performance.now() - startTime;
    // if (names.length > 20) {
    //   console.log(`Wheel draw time: ${duration.toFixed(2)}ms, ${names.length} names`);
    // }
  }
  
  export function spin() {
    if (isSpinning || names.length < 2) return;
    
    isSpinning = true;
    
    // Cache device checks for better performance
    const isMobile = window.innerWidth < 768;
    const isLowEnd = isMobile && navigator.deviceMemory && navigator.deviceMemory < 4;
    
    // Calculate random spin - reduce duration based on device capabilities
    const spinDuration = isLowEnd ? 2000 + Math.random() * 1000 : // Low-end device
                        isMobile ? 2500 + Math.random() * 1500 : // Normal mobile
                        3000 + Math.random() * 2000; // Desktop
    
    const spinRevolutions = isLowEnd ? 3 + Math.random() * 3 : // Low-end device
                           isMobile ? 4 + Math.random() * 4 : // Normal mobile
                           5 + Math.random() * 5; // Desktop
    
    const targetRotation = currentRotation + (spinRevolutions * 2 * Math.PI);
    const startTime = performance.now();
    const startRotation = currentRotation;
    
    // Optimized animation with frame skipping for better performance
    let lastFrameTime = 0;
    let frameCount = 0;
    let framesToSkip = isLowEnd ? 2 : (isMobile ? 1 : 0); // Skip frames on low-end devices
    
    function animate(time) {
      frameCount++;
      
      // Skip frames based on device performance
      if (framesToSkip > 0 && frameCount % (framesToSkip + 1) !== 0) {
        requestAnimationFrame(animate);
        return;
      }
      
      // Throttle to ~30fps on mobile devices
      if (isMobile && time - lastFrameTime < 33) {
        requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = time;
      
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / spinDuration, 1);
      
      // Optimized easing function for natural deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      currentRotation = startRotation + (targetRotation - startRotation) * easeOut;
      
      // Only redraw when needed - save CPU by not drawing final ~10% of animation frames
      // on mobile or when progress is very close to the end (avoiding unnecessary redraws)
      const skipEndFrames = progress > 0.9 && progress < 0.99 && isMobile;
      if (!skipEndFrames) {
        drawWheel();
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Always draw the final frame
        if (skipEndFrames) drawWheel();
        finishSpin();
      }
    }
    
    // Start animation with rAF for optimal timing
    requestAnimationFrame(animate);
  }
  
  async function finishSpin() {
    isSpinning = false;
    
    // Normalize rotation to 0-2π
    currentRotation = currentRotation % (2 * Math.PI);
    if (currentRotation < 0) currentRotation += 2 * Math.PI;
    
    // Determine winner
    const arc = (2 * Math.PI) / names.length;
    const pointerAngle = 1.5 * Math.PI; // Top position
    
    // Adjust the currentRotation to get the correct segment at the pointer
    let winnerIndex = Math.floor(((pointerAngle - currentRotation) % (2 * Math.PI)) / arc);
    if (winnerIndex < 0) winnerIndex += names.length;
    if (winnerIndex >= names.length) winnerIndex = 0;
    
    // Track the spin count in the database
    try {
      fetch('/api/stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error tracking spin:', error);
    }
    
    dispatch('winner', { winner: names[winnerIndex], index: winnerIndex });
  }
</script>

<div class="wheel-wrapper">
  <canvas 
    bind:this={canvas} 
    width={canvasSize} 
    height={canvasSize}
  ></canvas>
</div>

<style>
  .wheel-wrapper {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
  
  canvas {
    display: block;
    margin: 0 auto;
  }
</style>